import { assertUnreachable } from "common/utils/assertUnreachable";
import degreeToRadian from "common/utils/degreeToRadian";
import { aabb2ByArray, aabb2Center, aabb2Copy, aabb2Move, aabb2Scale } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { barycentricWeightsFromTriangle, interpolateWithBarycentricWeights } from "geom/barycentric";
import type { LineBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import type { HatchMaterial, HatchPatternLine } from "geom/hatchTypes";
import { HATCH_POINT_SIZE, HatchMaterialType, hatchPatternLineNew } from "geom/hatchTypes";
import { intersectPlaneRay } from "geom/intersect";
import { lineBSPNew } from "geom/lineBSPNew";
import { planeCopy, planeEval, planeNormal, planeProjectPolyLine3, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2FromAABB2,
  polyLine2Rotate,
  polyLine2Scale,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { ray2DotProject } from "geom/ray2";
import type { UVTransformation } from "geom/uv";
import { UVGeneration, applyUVs } from "geom/uv";
import hatchMaterialLibrary from "./hatchLibrary";

export interface GetHatchAABB2Params {
  plane: Plane;
  loop: PolyLine2;
  uvPlane?: Plane;
  uvGen?: UVGeneration;
  materialTransformation?: UVTransformation;
}

export function getHatchAABB2({
  plane,
  loop,
  uvPlane,
  uvGen = UVGeneration.NONE,
  materialTransformation,
}: GetHatchAABB2Params): { aabb2: AABB2; uvPlane: Plane } {
  const loop3 = polyLine2OnPlane(plane, loop);
  if (uvPlane === undefined) {
    uvPlane = applyUVs([], loop3, plane, uvGen, materialTransformation) ?? planeCopy(plane);
  }
  const aabb2 = polyLine2AABB2(planeProjectPolyLine3(uvPlane, loop3));

  return { aabb2, uvPlane };
}

function fillHatchPattern(patternLines: HatchPatternLine[], aabb2: AABB2): PolyLine2[] {
  const lines: PolyLine2[] = [];
  const { minX, minY, maxX, maxY } = aabb2;
  const aabbPoints: number[] = [minX, minY, maxX, minY, maxX, maxY, minX, maxY];

  for (let i = 0, l = patternLines.length; i < l; i++) {
    const { angle, originX, originY, deltaX, deltaY, incX, dash } = patternLines[i];
    const dashCount = dash.length;

    const sin = Math.sin(angle);
    const cos = Math.cos(angle);

    let startDotX = Number.MAX_VALUE;
    let startAabbPointX = -1;
    for (let j = 0; j < 4; j++) {
      const j2 = j * 2;
      const dist = ray2DotProject(originX, originY, cos, sin, aabbPoints[j2], aabbPoints[j2 + 1]);
      if (dist < startDotX) {
        startDotX = dist;
        startAabbPointX = j;
      }
    }

    const endAabbPointX = (startAabbPointX + 2) % 4;
    const startAabbPointY = (startAabbPointX + 1) % 4;
    const endAabbPointY = (startAabbPointX + 3) % 4;

    const endAabbPointX2 = endAabbPointX * 2;
    const startAabbPointY2 = startAabbPointY * 2;
    const endAabbPointY2 = endAabbPointY * 2;

    const endDotX = ray2DotProject(
      originX,
      originY,
      cos,
      sin,
      aabbPoints[endAabbPointX2],
      aabbPoints[endAabbPointX2 + 1],
    );
    const diffX = endDotX - startDotX;

    const startDotY = ray2DotProject(
      originX,
      originY,
      -sin,
      cos,
      aabbPoints[startAabbPointY2],
      aabbPoints[startAabbPointY2 + 1],
    );
    const endDotY = ray2DotProject(
      originX,
      originY,
      -sin,
      cos,
      aabbPoints[endAabbPointY2],
      aabbPoints[endAabbPointY2 + 1],
    );

    const lineId = Math.ceil(startDotY / deltaY);
    const startY = lineId * deltaY;
    const endY = Math.floor(endDotY / deltaY) * deltaY + DIST_EPSILON;
    const incY = Math.abs(deltaY);

    let offsetX = lineId * deltaX;
    for (let y = startY; y <= endY; y += incY) {
      const startX = dashCount > 0 ? Math.floor((startDotX - offsetX) / incX) * incX : startDotX;
      const endX = (dashCount > 0 ? Math.ceil((endDotX - offsetX) / incX) * incX : endDotX) + DIST_EPSILON;

      const lineStartX = originX + cos * offsetX - sin * y;
      const lineStartY = originY + sin * offsetX + cos * y;

      let x = startX;
      while (x <= endX) {
        let currentX = lineStartX + cos * x;
        let currentY = lineStartY + sin * x;

        if (dashCount > 0) {
          for (let d = 0; d < dashCount; d++) {
            const dashVal = dash[d];

            if (dashVal > DIST_EPSILON) {
              lines.push(polyLine2ByTwoPoints(currentX, currentY, currentX + cos * dashVal, currentY + sin * dashVal));
            } else if (dashVal > -DIST_EPSILON) {
              lines.push(
                polyLine2ByTwoPoints(
                  currentX,
                  currentY,
                  currentX + cos * HATCH_POINT_SIZE,
                  currentY + sin * HATCH_POINT_SIZE,
                ),
              );
            }

            const inc = Math.abs(dashVal);

            currentX += cos * inc;
            currentY += sin * inc;
          }

          x += incX;
        } else {
          lines.push(polyLine2ByTwoPoints(currentX, currentY, currentX + cos * diffX, currentY + sin * diffX));

          x += diffX;
        }
      }

      offsetX += deltaY < 0 ? -deltaX : deltaX;
    }
  }

  return lines;
}

function fillHatchDesign(
  sizeU: number,
  sizeV: number,
  rotation: number,
  designLines: PolyLine2[],
  aabb2: AABB2,
): PolyLine2[] {
  const lines: PolyLine2[] = [];

  const radians = degreeToRadian(rotation);

  const s = Math.sin(radians);
  const c = Math.cos(radians);

  const uX = c;
  const uY = s;
  const vX = -s;
  const vY = c;

  const [centerX, centerY] = aabb2Center(aabb2);
  const rotatedAABB = polyLine2AABB2(polyLine2Rotate(polyLine2FromAABB2(aabb2), radians, centerX, centerY));

  let { minX, minY, maxX, maxY } = rotatedAABB;

  // Add a bit of padding to be conservative.
  minX -= DIST_EPSILON;
  minY -= DIST_EPSILON;
  maxX += DIST_EPSILON;
  maxY += DIST_EPSILON;

  // get the origin point
  let originX = (minX - centerX) * c - (minY - centerY) * s + centerX;
  let originY = (minX - centerX) * s + (minY - centerY) * c + centerY;

  // round the origin point
  let u = ray2DotProject(0, 0, uX, uY, originX, originY);
  let v = ray2DotProject(0, 0, vX, vY, originX, originY);

  u = Math.floor(u / sizeU) * sizeU;
  v = Math.floor(v / sizeV) * sizeV;

  originX = uX * u + vX * v;
  originY = uY * u + vY * v;

  // get the draw range
  maxX -= minX;
  maxY -= minY;

  minX = 0;
  minY = 0;

  // -1 since hatch pattern lines are allowed to cross the tile boundary on one side.
  const startX = (Math.floor(minX / sizeU) - 1) * sizeU;
  const endX = Math.ceil(maxX / sizeU) * sizeU + DIST_EPSILON;

  const startY = (Math.floor(minY / sizeV) - 1) * sizeV;
  const endY = Math.ceil(maxY / sizeV) * sizeV + DIST_EPSILON;

  // draw lines
  const lineCount = designLines.length;
  for (let y = startY; y <= endY; y += sizeV) {
    for (let x = startX; x <= endX; x += sizeU) {
      for (let i = 0; i < lineCount; i++) {
        const line = designLines[i];
        for (let p = 0, lp = line.length - 2; p < lp; p += 2) {
          const ax = x + line[p];
          const ay = y + line[p + 1];
          const bx = x + line[p + 2];
          const by = y + line[p + 3];

          lines.push(
            polyLine2ByTwoPoints(
              originX + ax * c - ay * s,
              originY + ax * s + ay * c,
              originX + bx * c - by * s,
              originY + bx * s + by * c,
            ),
          );
        }
      }
    }
  }

  return lines;
}

export function fillHatchLines2D(hatchMaterial: HatchMaterial, unscaledAABB: AABB2, scaleFactor?: number): PolyLine2[] {
  // Note: ideally we could pass the scale factor to the hatch function instead of rescaling here.
  const aabb2 = scaleFactor ? aabb2Scale(unscaledAABB, scaleFactor) : unscaledAABB;
  if (aabb2Width(aabb2) < DIST_EPSILON || aabb2Height(aabb2) < DIST_EPSILON) {
    return [];
  }

  let lines: PolyLine2[] = [];

  switch (hatchMaterial.type) {
    case HatchMaterialType.PROCEDURAL: {
      const src = hatchMaterialLibrary[hatchMaterial.materialId];

      if (src.type !== "PROCEDURAL") {
        console.warn("Invalid hatch material type in library for: " + hatchMaterial.materialId);
        return [];
      }

      lines = src.fillFunction(aabb2);
      break;
    }
    case HatchMaterialType.PATTERN: {
      lines = fillHatchPattern(hatchMaterial.patternLines, aabb2);
      break;
    }
    case HatchMaterialType.DESIGN: {
      lines = fillHatchDesign(
        hatchMaterial.sizeU,
        hatchMaterial.sizeV,
        hatchMaterial.rotation,
        hatchMaterial.lines,
        aabb2,
      );
      break;
    }
    default: {
      assertUnreachable(hatchMaterial);
    }
  }

  return scaleFactor ? lines.map((pl) => polyLine2Scale(pl, 1 / scaleFactor)) : lines;
}

export function fillHatchLines(
  hatchMaterial: HatchMaterial,
  uvPlane: Plane,
  facePlane: Plane,
  aabb2: AABB2,
): LineBSP[] {
  const lines2D = fillHatchLines2D(hatchMaterial, aabb2);
  const lines: LineBSP[] = new Array<LineBSP>(lines2D.length);

  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = facePlane;
  const [nX, nY, nZ] = planeNormal(uvPlane);

  let lineCount = 0;

  for (let i = 0, l = lines2D.length; i < l; i++) {
    const line2D = lines2D[i];

    const [x0, y0, z0] = planeEval(uvPlane, line2D[0], line2D[1]);
    const [x1, y1, z1] = planeEval(uvPlane, line2D[2], line2D[3]);

    /**
     * intersectPlaneRay returns a reference to vec2Result, so we need
     * to destructure it immediately to avoid overwriting it. That's why
     * we do the null check in 2 steps.
     */

    const int0 = intersectPlaneRay(facePlane, x0, y0, z0, nX, nY, nZ);
    if (!int0) {
      continue;
    }
    const [ax, ay] = int0;

    const int1 = intersectPlaneRay(facePlane, x1, y1, z1, nX, nY, nZ);
    if (!int1) {
      continue;
    }
    const [bx, by] = int1;

    lines[lineCount] = lineBSPNew(
      originX + uX * ax + vX * ay,
      originY + uY * ax + vY * ay,
      originZ + uZ * ax + vZ * ay,
      originX + uX * bx + vX * by,
      originY + uY * bx + vY * by,
      originZ + uZ * bx + vZ * by,
    );
    ++lineCount;
  }
  lines.length = lineCount;

  return lines;
}

/**
 * Fill hatch lines inside a triangle where each vertex has explicitly defined UV coordinates.
 */
export function fillHatchLinesUv(hatchMaterial: HatchMaterial, vertices: number[], uvCoordinates: number[]): LineBSP[] {
  const aabb2: AABB2 | null = aabb2ByArray([uvCoordinates]);
  if (!aabb2) {
    return [];
  }
  const lines2D = fillHatchLines2D(hatchMaterial, aabb2);
  const lines: LineBSP[] = new Array<LineBSP>(lines2D.length);

  const [x0, y0, z0, x1, y1, z1, x2, y2, z2] = vertices;

  for (let i = 0, l = lines2D.length; i < l; i++) {
    const line2D = lines2D[i];
    const [w0, w1, w2] = barycentricWeightsFromTriangle(
      uvCoordinates[0],
      uvCoordinates[1],
      uvCoordinates[2],
      uvCoordinates[3],
      uvCoordinates[4],
      uvCoordinates[5],
      line2D[0],
      line2D[1],
    );
    const [w3, w4, w5] = barycentricWeightsFromTriangle(
      uvCoordinates[0],
      uvCoordinates[1],
      uvCoordinates[2],
      uvCoordinates[3],
      uvCoordinates[4],
      uvCoordinates[5],
      line2D[2],
      line2D[3],
    );
    lines[i] = lineBSPNew(
      interpolateWithBarycentricWeights(x0, x1, x2, w0, w1, w2),
      interpolateWithBarycentricWeights(y0, y1, y2, w0, w1, w2),
      interpolateWithBarycentricWeights(z0, z1, z2, w0, w1, w2),
      interpolateWithBarycentricWeights(x0, x1, x2, w3, w4, w5),
      interpolateWithBarycentricWeights(y0, y1, y2, w3, w4, w5),
      interpolateWithBarycentricWeights(z0, z1, z2, w3, w4, w5),
    );
  }

  return lines;
}

// Draws hatch material lines on canvas 2D using the current canvas stroke style.
export function fillHatchLinesOnCanvas(
  ctx: CanvasRenderingContext2D,
  hatchMaterial: HatchMaterial,
  aabb2: AABB2,
  worldWidth: number,
  worldOffsetX: number,
  worldOffsetY: number,
  showDebugVertices: boolean = false,
): void {
  const { minY, maxY } = aabb2;
  const aabbWidth = aabb2Width(aabb2);
  const scale = aabbWidth / worldWidth;
  const hatchAabb2 = aabb2Scale(aabb2Copy(aabb2), 1 / scale);
  aabb2Move(hatchAabb2, worldOffsetX, worldOffsetY);
  const lines = fillHatchLines2D(hatchMaterial, hatchAabb2);

  ctx.beginPath();
  for (let i = 0, l = lines.length; i < l; i++) {
    const line = lines[i];
    ctx.moveTo((line[0] - worldOffsetX) * scale, maxY - (line[1] - worldOffsetY) * scale + minY);
    ctx.lineTo((line[2] - worldOffsetX) * scale, maxY - (line[3] - worldOffsetY) * scale + minY);
  }
  ctx.stroke();

  if (showDebugVertices) {
    ctx.strokeStyle = "red";
    for (let i = 0, l = lines.length; i < l; i++) {
      const line = lines[i];
      ctx.beginPath();
      ctx.arc((line[0] - worldOffsetX) * scale, maxY - (line[1] - worldOffsetY) * scale, 3, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc((line[2] - worldOffsetX) * scale, maxY - (line[3] - worldOffsetY) * scale, 3, 0, 2 * Math.PI);
      ctx.stroke();
    }
  }
}

export function parseHatchPattern(
  pattern: string,
  scale: number = 1.0,
  offsetX: number = 0.0,
  offsetY: number = 0.0,
  angle: number = 0.0,
): HatchPatternLine[] {
  const hatchPatternLines: HatchPatternLine[] = [];

  if (!pattern) {
    return hatchPatternLines;
  }

  let lookForEndOfLine = false;
  let numbers: number[] = [];
  let currentNumber: string = "";

  const addCurrentNumber = () => {
    numbers.push(parseFloat(currentNumber));
    currentNumber = "";
  };

  const addCurrentLine = () => {
    if (!lookForEndOfLine) {
      addCurrentNumber();
      if (numbers.length >= 5) {
        hatchPatternLines.push(
          hatchPatternLineNew(
            degreeToRadian(numbers[0] + angle),
            numbers[1] * scale + offsetX,
            numbers[2] * scale + offsetY,
            numbers[3] * scale,
            numbers[4] * scale,
            numbers.slice(5).map((x) => {
              return x * scale;
            }),
          ),
        );
      }
    }
  };

  for (let i = 0, l = pattern.length; i < l; i++) {
    const c = pattern[i];
    if (c === "*" || c === ";") {
      lookForEndOfLine = true;
    } else if (c === "\n") {
      addCurrentLine();
      numbers = [];
      lookForEndOfLine = false;
    } else if (!lookForEndOfLine) {
      if (c === ",") {
        addCurrentNumber();
      } else {
        currentNumber += c;
      }
    }
  }

  addCurrentLine();

  return hatchPatternLines;
}
